import React, { useState } from 'react';
import { TextArea } from '@fullstory/components';
import { useNotifications } from '@fullstory/react-hooks';
import { useIntl } from 'react-intl';

export interface SnippetTextAreaProps
  extends Pick<React.ComponentProps<typeof TextArea>, 'size' | 'onCopyButtonClicked' | 'rows'> {
  /** The copyable code snippet */
  snippet: string;
  /** Will respect rows prop unless focused. On focus, will show entire snippet. */
  collapsible?: boolean;
}

const getNumLines = (s: string) => s.split(/\n/).length + 2;

export const SnippetTextArea: React.FC<SnippetTextAreaProps> = ({
  snippet,
  rows = getNumLines(snippet),
  size = 'small',
  collapsible = false,
  onCopyButtonClicked,
}) => {
  const intl = useIntl();
  const [numRows, setNumRows] = useState(rows);
  const notifications = useNotifications();
  const { success, error } = onCopyButtonClicked ?? {
    success: () =>
      notifications.success(
        intl.formatMessage({ defaultMessage: 'Copied to clipboard.', id: '8qZ4HE' }),
      ),
    error: () =>
      notifications.error(
        intl.formatMessage({ defaultMessage: 'Failed to copy to clipboard.', id: 'kbVIef' }),
      ),
  };
  const focusProps = collapsible
    ? {
        onFocus: () => setNumRows(getNumLines(snippet)),
        onBlur: () => setNumRows(rows),
      }
    : {};

  return (
    <TextArea
      {...focusProps}
      value={snippet}
      rows={numRows}
      copyable="always"
      readOnly
      fontFamily="mono"
      size={size}
      onCopyButtonClicked={{ success, error }}
    />
  );
};
